import h from 'hyperscript';

import { label } from 'cadenza/utils/aria';
import { assert } from 'cadenza/utils/custom-error';
import { scrollIntoView } from 'cadenza/utils/scroll-into-view';
import { uniqueId } from 'cadenza/utils/unique-id';
import { ChangeEvent, platformModifierKey } from 'cadenza/utils/event-util';

import './select-list.css';

export const UNSELECTABLE_OPTION_CLASS = 'unselectable-option';
const COMPONENT_NAME = 'd-select-list';

interface SelectListConfig {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  multiple?: boolean;
}

interface SelectListOptionItem<T> {
  value: T;
  label: string;
  selected?: boolean;
}

export type SelectListOptionElement<T> = Element & { selectListValue: T };

type SelectListOption<T> = (T & string) | SelectListOptionItem<T> | SelectListOptionElement<T>;

type V<T> = T extends undefined ? Element : T;

function val<T>(option: Element) {
  return ('selectListValue' in option ? option.selectListValue : option) as V<T>;
}

/**
 * The selectable options can be specified in three different ways:
 * - A simple string, which will be used for both the value and label
 * - A simple object with value, label and optionally selected properties
 * - A DOM element which will be inserted directly into the DOM as a child of the select list.
 * In this case the element should have a selectListValue property that contains the value that will
 * be used as the selection value when the item is selected.
 *
 * In some cases, because of some legacy usages that are hard to refactor (attribute lists),
 * the element itself is returned as the selection. In this, case it doesn't need the
 * selectListValue property, and the generic type of the SelectList should be the type of
 * the component that is used as the option elements.
 *
 * We should try to get rid of this weird special case and, in any instance, not introduce
 * any new weird usage of this type.
 */
export class SelectList<T> extends HTMLElement {
  constructor(
    options: (T extends undefined ? Element : SelectListOption<T>)[] = [],
    { ariaLabel, ariaLabelledBy, multiple = false }: SelectListConfig = {},
  ) {
    assert(options.length >= 1, 'There must be at least one option');

    super();
    this.classList.add(COMPONENT_NAME, 'form-group-control');
    this.setAttribute('role', 'listbox');
    this.tabIndex = 0;
    label(this, { ariaLabel, ariaLabelledBy });
    this.append(...options.map(createOrAdaptOptionElement));

    if (multiple) {
      this.setAttribute('aria-multiselectable', 'true');
    } else {
      this.selection = this.selection.slice(-1); // select the last selected option
    }

    this.addEventListener('focus', () => this.#onFocus());
    this.addEventListener('blur', () => this.#onBlur());
    this.addEventListener('click', (event) => this.#onClick(event));
    this.addEventListener('keydown', (event) => this.#onKeyDown(event));
  }

  #onClick(event: MouseEvent) {
    const option = (event.target as Element)?.closest('[role=option]');
    if (option && !option.classList.contains(UNSELECTABLE_OPTION_CLASS)) {
      this.#toggleSelected(
        option,
        this.multiple ? undefined : true, // cannot deselect option in single selection mode
        platformModifierKey(event),
      );
      this.#setActive(option);
    }
  }

  #onFocus() {
    if (!this.#getActive()) {
      this.#setActive(this.#selectedOptionElements[0] || this.children[0]);
    }
  }

  #onBlur() {
    this.#resetActive(); // Removes is-active class on children and aria-activedescendant on this
  }

  #onKeyDown(event: KeyboardEvent) {
    const key = event.key;
    const newActiveOption = this.#handleKeyboardNavigation(key);

    if (newActiveOption) {
      event.preventDefault(); // prevent scrolling
      this.#setActive(newActiveOption);

      // in single mode, with ctrl/cmd pressed, change only the focused option, otherwise change the selection too
      if (!this.multiple && !platformModifierKey(event)) {
        this.#toggleSelected(newActiveOption, true);
      }
    }

    if (key === ' ') {
      event.preventDefault(); // prevent scrolling
      const currentlyActiveOption = this.#getActive();
      if (currentlyActiveOption) {
        // The space key toggles the current active option selection status.
        // In single mode, it always selects it (no deselection) and replaces the currently selected one.
        // In multiple mode, we allow deselection and already selected options remain selected.
        this.#toggleSelected(currentlyActiveOption, this.multiple ? undefined : true, this.multiple);
      }
    }
  }

  /**
   * Checks if the given key (from a keyboard event) should trigger keyboard navigation,
   * and if so determines the newly focused option.
   */
  #handleKeyboardNavigation(key: string) {
    const previousActiveOption = this.#getActive();

    switch (key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        return this.#getPreviousSelectableSibling(previousActiveOption);
      case 'ArrowDown':
      case 'ArrowRight':
        return this.#getNextSelectableSibling(previousActiveOption);
      case 'Home':
        return this.#getFirstSelectableOption();
      case 'End':
        return this.#getLastSelectableOption();
    }
  }

  #getActive(): Element | null {
    const activeId = this.getAttribute('aria-activedescendant');
    return activeId ? this.querySelector(`[id="${activeId}"]`) : null;
  }

  #setActive(option: Element) {
    const previousActive = this.#getActive();
    if (option && option !== previousActive) {
      if (previousActive) {
        previousActive.classList.remove('is-active');
      }
      this.setAttribute('aria-activedescendant', option.id);
      option.classList.add('is-active');
      scrollIntoView(option);
    }
  }

  get multiple() {
    return this.getAttribute('aria-multiselectable') === 'true';
  }

  get selection() {
    return this.#selectedOptionElements.map((el) => val<T>(el));
  }

  set selection(selection: V<T>[]) {
    this.#resetSelection();
    for (const option of this.children) {
      const selected = selection.includes(val(option));
      option.setAttribute('aria-selected', String(selected));
      if (selected && !this.multiple) {
        this.#setActive(option);
        return;
      }
    }
  }

  #resetSelection() {
    this.querySelectorAll('[aria-selected=true]').forEach((el) => el.removeAttribute('aria-selected'));
  }

  #resetActive() {
    this.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    this.removeAttribute('aria-activedescendant');
  }

  #toggleSelected(optionElement: Element, force?: boolean, preserveSelection = false) {
    const previouslySelected = optionElement.getAttribute('aria-selected') === 'true';

    if (!(this.multiple && preserveSelection)) {
      this.#selectedOptionElements.forEach((o) => o.setAttribute('aria-selected', 'false'));
    }

    const selected = force ?? !previouslySelected;
    optionElement.setAttribute('aria-selected', String(selected));

    if (selected !== previouslySelected) {
      this.dispatchEvent(new ChangeEvent());
    }
  }

  #getNextSelectableSibling(previousActiveOption: Element | null): Element {
    if (!previousActiveOption) {
      return this.#getFirstSelectableOption();
    }

    const nextSibling = previousActiveOption.nextElementSibling;
    if (!nextSibling) {
      return previousActiveOption;
    }
    return nextSibling.classList.contains(UNSELECTABLE_OPTION_CLASS) ?
        this.#getNextSelectableSibling(nextSibling)
      : nextSibling;
  }

  #getPreviousSelectableSibling(previousActiveOption: Element | null): Element {
    if (!previousActiveOption) {
      return this.#getFirstSelectableOption();
    }
    const previousSibling = previousActiveOption.previousElementSibling;
    if (!previousSibling) {
      return previousActiveOption;
    }
    return previousSibling.classList.contains(UNSELECTABLE_OPTION_CLASS) ?
        this.#getPreviousSelectableSibling(previousSibling)
      : previousSibling;
  }

  #getFirstSelectableOption() {
    const firstChild = this.firstElementChild!;
    return firstChild.classList.contains(UNSELECTABLE_OPTION_CLASS) ?
        this.#getNextSelectableSibling(firstChild)
      : firstChild;
  }

  #getLastSelectableOption() {
    const lastChild = this.lastElementChild!;
    return lastChild.classList.contains(UNSELECTABLE_OPTION_CLASS) ?
        this.#getPreviousSelectableSibling(lastChild)
      : lastChild;
  }

  get #selectedOptionElements(): Element[] {
    return [...this.querySelectorAll('[aria-selected=true]')];
  }
}

customElements.define(COMPONENT_NAME, SelectList);

function createOrAdaptOptionElement<T>(option: SelectListOption<T> | Element): Element {
  if (typeof option === 'string') {
    option = { value: option, label: option };
  }
  if (!(option instanceof Element)) {
    const { value, label, selected = false } = option;
    option = h<SelectListOptionElement<T>>(
      `li.${COMPONENT_NAME}--option.ellipsis`,
      {
        selectListValue: value,
        attrs: { 'aria-selected': String(selected) },
      },
      label,
    );
  }

  option.id ||= uniqueId();
  option.setAttribute('role', 'option');
  return option;
}
